/**
 * Content detectors. A detector turns text into a match count — never into the
 * matched values themselves leaving this module. The only data that escapes is
 * {detector id, count, confidence}. Future detector kinds (document fingerprinting,
 * exact-data-match, local ML classifiers) implement the same `ContentDetector`
 * interface, so classifications and the policy engine do not change.
 */

export type Confidence = 'low' | 'medium' | 'high';

export interface DetectorHit {
  detector: string;
  count: number;
  confidence: Confidence;
}

export interface ContentDetector {
  readonly id: string;
  readonly name: string;
  readonly kind: 'builtin' | 'regex' | 'dictionary' | 'dynamic';
  scan(text: string): DetectorHit | null;
}

/** Hard cap on matches counted per detector — bounds work on adversarial input. */
const MAX_MATCHES_PER_DETECTOR = 10_000;

function countMatches(re: RegExp, text: string, accept?: (m: RegExpExecArray) => boolean): number {
  if (!re.global) throw new Error('detector regex must be global');
  re.lastIndex = 0;
  let n = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    if (m[0].length === 0) {
      re.lastIndex++;
      continue;
    }
    if (!accept || accept(m)) {
      n++;
      if (n >= MAX_MATCHES_PER_DETECTOR) break;
    }
  }
  return n;
}

export function luhnValid(digits: string): boolean {
  let sum = 0;
  let dbl = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    if (dbl) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    dbl = !dbl;
  }
  return sum % 10 === 0;
}

/** Issuer identification: only accept prefixes of real card networks. */
function knownCardNetwork(d: string): boolean {
  const len = d.length;
  const p2 = Number(d.slice(0, 2));
  const p3 = Number(d.slice(0, 3));
  const p4 = Number(d.slice(0, 4));
  const p6 = Number(d.slice(0, 6));
  if (d[0] === '4') return len === 13 || len === 16 || len === 19; // Visa
  if ((p2 >= 51 && p2 <= 55) || (p4 >= 2221 && p4 <= 2720)) return len === 16; // Mastercard
  if (p2 === 34 || p2 === 37) return len === 15; // Amex
  if (p4 === 6011 || p2 === 65 || (p3 >= 644 && p3 <= 649) || (p6 >= 622126 && p6 <= 622925))
    return len >= 16 && len <= 19; // Discover
  if (p4 >= 3528 && p4 <= 3589) return len >= 16 && len <= 19; // JCB
  if (p2 === 36 || p2 === 38 || p2 === 39 || (p3 >= 300 && p3 <= 305)) return len >= 14 && len <= 19; // Diners
  if (p2 === 62) return len >= 16 && len <= 19; // UnionPay
  return false;
}

function shannonEntropy(s: string): number {
  const freq = new Map<string, number>();
  for (const ch of s) freq.set(ch, (freq.get(ch) ?? 0) + 1);
  let h = 0;
  for (const c of freq.values()) {
    const p = c / s.length;
    h -= p * Math.log2(p);
  }
  return h;
}

interface RegexSpec {
  id: string;
  name: string;
  re: RegExp;
  confidence: Confidence;
  accept?: (m: RegExpExecArray) => boolean;
}

function regexDetector(spec: RegexSpec): ContentDetector {
  return {
    id: spec.id,
    name: spec.name,
    kind: 'builtin',
    scan(text) {
      const count = countMatches(spec.re, text, spec.accept);
      return count > 0 ? { detector: spec.id, count, confidence: spec.confidence } : null;
    },
  };
}

const SSN_EXAMPLES = new Set(['123456789', '078051120', '219099999']);

const BUILTIN_SPECS: RegexSpec[] = [
  {
    id: 'pci.card',
    name: 'Payment card number',
    // 13-19 digits optionally separated by single spaces or dashes; validated below.
    re: /(?<![\d-])(?:\d[ -]?){12,18}\d(?![\d-])/g,
    confidence: 'high',
    accept: (m) => {
      const raw = m[0];
      const seps = raw.match(/[ -]/g);
      // Separators, when present, must be consistent (all spaces or all dashes).
      if (seps && new Set(seps).size > 1) return false;
      const d = raw.replace(/[ -]/g, '');
      if (d.length < 13 || d.length > 19) return false;
      if (/^(\d)\1+$/.test(d)) return false;
      return knownCardNetwork(d) && luhnValid(d);
    },
  },
  {
    id: 'pii.us_ssn',
    name: 'U.S. Social Security number',
    re: /(?<![\d-])(?!000|666|9\d\d)(\d{3})([- ])(?!00)(\d{2})\2(?!0000)(\d{4})(?![\d-])|\b(?:ssn|social security(?: number)?)\W{0,5}(?!000|666|9\d\d)(\d{3})(?!00)(\d{2})(?!0000)(\d{4})\b/gi,
    confidence: 'high',
    accept: (m) => {
      const d = m[1] ? `${m[1]}${m[3]}${m[4]}` : `${m[5]}${m[6]}${m[7]}`;
      return !SSN_EXAMPLES.has(d);
    },
  },
  {
    id: 'pii.email',
    name: 'Email address',
    re: /\b[A-Za-z0-9._%+-]{1,64}@(?:[A-Za-z0-9-]{1,63}\.){1,8}[A-Za-z]{2,24}\b/g,
    confidence: 'high',
  },
  {
    id: 'pii.phone_us',
    name: 'U.S. phone number',
    // Requires separators so bare digit runs (order ids, timestamps) do not match.
    re: /(?<![\d\w])(?:\+?1[ .-]?)?(?:\([2-9]\d{2}\)\s?|[2-9]\d{2}[ .-])[2-9]\d{2}[ .-]\d{4}(?![\d\w])/g,
    confidence: 'medium',
  },
  {
    id: 'secret.aws_access_key',
    name: 'AWS access key ID',
    re: /\b(?:AKIA|ASIA|ABIA|ACCA)[A-Z0-9]{16}\b/g,
    confidence: 'high',
  },
  {
    id: 'secret.aws_secret_key',
    name: 'AWS secret access key',
    re: /aws_?secret_?(?:access_?)?key["']?\s{0,3}[:=]\s{0,3}["']?[A-Za-z0-9/+=]{40}(?![A-Za-z0-9/+=])/gi,
    confidence: 'high',
  },
  {
    id: 'secret.github_token',
    name: 'GitHub token',
    re: /\b(?:gh[pousr]_[A-Za-z0-9]{36,255}|github_pat_[A-Za-z0-9_]{22,255})\b/g,
    confidence: 'high',
  },
  {
    id: 'secret.slack_token',
    name: 'Slack token',
    re: /\bxox[abposr]-[A-Za-z0-9-]{10,200}\b/g,
    confidence: 'high',
  },
  {
    id: 'secret.google_api_key',
    name: 'Google API key',
    re: /\bAIza[0-9A-Za-z_-]{35}\b/g,
    confidence: 'high',
  },
  {
    id: 'secret.stripe_key',
    name: 'Stripe secret key',
    re: /\b(?:sk|rk)_live_[0-9A-Za-z]{20,99}\b/g,
    confidence: 'high',
  },
  {
    id: 'secret.azure_storage_key',
    name: 'Azure storage connection string',
    re: /AccountName=[^;\s]{1,64};\s?AccountKey=[A-Za-z0-9+/]{40,100}={0,2}/g,
    confidence: 'high',
  },
  {
    id: 'secret.gcp_service_account',
    name: 'Google Cloud service account key',
    re: /"type"\s{0,3}:\s{0,3}"service_account"/g,
    confidence: 'high',
  },
  {
    id: 'secret.private_key',
    name: 'Private key material',
    re: /-----BEGIN (?:RSA |EC |DSA |OPENSSH |ENCRYPTED |PGP )?PRIVATE KEY(?: BLOCK)?-----/g,
    confidence: 'high',
  },
  {
    id: 'secret.db_connection_string',
    name: 'Database connection string with credentials',
    re: /\b(?:postgres(?:ql)?|mysql|mariadb|mongodb(?:\+srv)?|rediss?|amqps?|mssql|sqlserver):\/\/[^\s:@/]{1,100}:[^\s@/]{1,200}@[^\s/]{1,253}|\b(?:Server|Data Source)=[^;\n]{1,200};[^\n]{0,300}?\b(?:Password|Pwd)=[^;\s]{1,200}/gi,
    confidence: 'high',
  },
  {
    id: 'secret.jwt',
    name: 'JSON Web Token',
    re: /\beyJ[A-Za-z0-9_-]{8,2000}\.eyJ[A-Za-z0-9_-]{8,8000}\.[A-Za-z0-9_-]{8,2000}\b/g,
    confidence: 'medium',
  },
  {
    id: 'secret.generic',
    name: 'Credential assignment',
    re: /\b(?:api[_-]?key|secret[_-]?key|client[_-]?secret|access[_-]?token|auth[_-]?token|passw(?:or)?d)["']?\s{0,3}[:=]\s{0,3}["']?([A-Za-z0-9/+_\-.=!@#$%^&*]{12,200})/gi,
    confidence: 'medium',
    accept: (m) => {
      const v = m[1] ?? '';
      // Reject placeholders and low-entropy words ("password=changeme12345").
      if (/^(?:x+|\*+|<.*>|\$\{.*\}|your[_-]?.*|changeme.*|example.*|placeholder.*)$/i.test(v)) return false;
      return /\d/.test(v) && /[A-Za-z]/.test(v) && shannonEntropy(v) >= 3.2;
    },
  },
];

export const BUILTIN_DETECTORS: readonly ContentDetector[] = BUILTIN_SPECS.map(regexDetector);

export const BUILTIN_DETECTOR_INFO: ReadonlyArray<{ id: string; name: string; confidence: Confidence }> =
  BUILTIN_SPECS.map((s) => ({ id: s.id, name: s.name, confidence: s.confidence }));

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\/-]/g, '\\$&');
}

/** Emails / hostnames under the organisation's own domains. */
export function corporateReferenceDetector(domains: readonly string[]): ContentDetector | null {
  const clean = domains.map((d) => d.trim().toLowerCase()).filter((d) => /^[a-z0-9.-]{3,253}$/.test(d));
  if (clean.length === 0) return null;
  const alt = clean.map(escapeRegex).join('|');
  const re = new RegExp(`(?<![A-Za-z0-9.-])(?:[A-Za-z0-9._%+-]{1,64}@|(?:[A-Za-z0-9-]{1,63}\\.){0,6})(?:${alt})(?![A-Za-z0-9-])`, 'gi');
  return {
    id: 'internal.corporate_reference',
    name: 'Internal domain reference',
    kind: 'dynamic',
    scan(text) {
      const count = countMatches(re, text);
      return count > 0 ? { detector: this.id, count, confidence: 'medium' } : null;
    },
  };
}

export function dictionaryDetector(def: {
  id: string;
  name: string;
  terms: readonly string[];
  caseInsensitive: boolean;
  wholeWord: boolean;
}): ContentDetector {
  // Longest-first so overlapping terms prefer the most specific match. A pure
  // alternation of escaped literals cannot backtrack catastrophically.
  const terms = [...new Set(def.terms.map((t) => t.trim()).filter((t) => t.length >= 2))].sort(
    (a, b) => b.length - a.length,
  );
  const body = terms.map(escapeRegex).join('|');
  const src = def.wholeWord ? `(?<![\\p{L}\\p{N}_])(?:${body})(?![\\p{L}\\p{N}_])` : `(?:${body})`;
  const re = new RegExp(src, def.caseInsensitive ? 'giu' : 'gu');
  return {
    id: def.id,
    name: def.name,
    kind: 'dictionary',
    scan(text) {
      if (terms.length === 0) return null;
      const count = countMatches(re, text);
      return count > 0 ? { detector: def.id, count, confidence: 'medium' } : null;
    },
  };
}

export function regexCustomDetector(def: {
  id: string;
  name: string;
  pattern: string;
  caseInsensitive: boolean;
}): ContentDetector {
  const check = validateCustomRegex(def.pattern);
  if (!check.ok) throw new Error(`unsafe regex for detector ${def.id}: ${check.reason}`);
  const re = new RegExp(def.pattern, def.caseInsensitive ? 'gi' : 'g');
  return {
    id: def.id,
    name: def.name,
    kind: 'regex',
    scan(text) {
      const count = countMatches(re, text.length > CUSTOM_DETECTOR_MAX_CHARS ? text.slice(0, CUSTOM_DETECTOR_MAX_CHARS) : text);
      return count > 0 ? { detector: def.id, count, confidence: 'medium' } : null;
    },
  };
}

/**
 * Conservative ReDoS guard for administrator-defined patterns. JavaScript regexes have
 * no execution timeout and these run on the main thread of users' browsers, so the rule
 * set is deliberately strict — rejecting a legitimate-but-risky pattern is acceptable,
 * accepting a catastrophic one is not:
 *  - no backreferences or lookbehind;
 *  - a group that contains a quantifier or alternation may only be repeated a small,
 *    bounded number of times ({n,m} with m ≤ 5, or ?) — `(a|a){1,99}` and `(a+)+` fail;
 *  - two adjacent atoms may not both be unbounded (`\s*\s*`, `a*a*`, `\w+\s*\w+`);
 *    use bounded repetition such as {1,50} instead;
 *  - at most 3 unbounded quantifiers overall; must not match the empty string.
 * Custom detectors additionally see at most CUSTOM_DETECTOR_MAX_CHARS of input.
 */
export function validateCustomRegex(pattern: string): { ok: true } | { ok: false; reason: string } {
  if (pattern.length > 300) return { ok: false, reason: 'pattern longer than 300 characters' };
  try {
    new RegExp(pattern, 'g');
  } catch {
    return { ok: false, reason: 'pattern does not compile' };
  }
  if (/\\[1-9]|\\k</.test(pattern)) return { ok: false, reason: 'backreferences are not allowed' };
  if (/\(\?<[=!]/.test(pattern)) return { ok: false, reason: 'lookbehind is not allowed' };
  if (new RegExp(pattern).test('')) return { ok: false, reason: 'pattern matches empty text' };

  type Frame = { risky: boolean; prevUnbounded: boolean };
  const stack: Frame[] = [{ risky: false, prevUnbounded: false }];
  let unboundedCount = 0;
  let i = 0;
  // Reads an optional quantifier at position j; returns [max (Infinity if unbounded) | null, next index].
  const quant = (j: number): [number | null, number] => {
    const ch = pattern[j];
    let max: number | null = null;
    let k = j;
    if (ch === '*' || ch === '+') [max, k] = [Infinity, j + 1];
    else if (ch === '?') [max, k] = [1, j + 1];
    else if (ch === '{') {
      const m = /^\{(\d+)(,(\d*))?\}/.exec(pattern.slice(j));
      if (m) [max, k] = [m[2] === undefined ? Number(m[1]) : m[3] === '' ? Infinity : Number(m[3]), j + m[0].length];
    }
    if (max !== null && pattern[k] === '?') k++; // lazy modifier
    return [max, k];
  };
  // Applies a quantifier to the atom just read; returns an error or null.
  const apply = (max: number | null, atomRisky: boolean): string | null => {
    const top = stack[stack.length - 1]!;
    const unbounded = max === Infinity;
    if (max !== null && max > 1) top.risky = true;
    if (atomRisky && max !== null && max > 5) return 'repeated group contains a quantifier or alternation';
    if (unbounded) {
      unboundedCount++;
      if (top.prevUnbounded) return 'adjacent unbounded quantifiers (use a bounded range such as {1,50})';
    }
    top.prevUnbounded = unbounded;
    if (atomRisky) top.risky = true;
    return null;
  };
  while (i < pattern.length) {
    const ch = pattern[i]!;
    if (ch === '|') {
      const top = stack[stack.length - 1]!;
      top.risky = true;
      top.prevUnbounded = false;
      i++;
      continue;
    }
    if (ch === '(') {
      stack.push({ risky: false, prevUnbounded: false });
      i += pattern[i + 1] === '?' ? (pattern[i + 2] === '<' ? 4 : 3) : 1;
      if (pattern[i - 1] === '<') {
        // named group: skip the name
        const close = pattern.indexOf('>', i);
        i = close < 0 ? i : close + 1;
      }
      continue;
    }
    if (ch === ')') {
      const grp = stack.pop();
      if (!grp || stack.length === 0) return { ok: false, reason: 'unbalanced parentheses' };
      const [max, next] = quant(i + 1);
      const err = apply(max, grp.risky);
      if (err) return { ok: false, reason: err };
      i = next;
      continue;
    }
    // A single atom: escape, class, dot, or literal.
    let end = i + 1;
    if (ch === '\\') end = i + 2;
    else if (ch === '[') {
      end = i + 1;
      while (end < pattern.length && pattern[end] !== ']') end += pattern[end] === '\\' ? 2 : 1;
      end++;
    } else if (ch === '^' || ch === '$') {
      stack[stack.length - 1]!.prevUnbounded = false;
      i++;
      continue;
    }
    const [max, next] = quant(end);
    const err = apply(max, false);
    if (err) return { ok: false, reason: err };
    i = next;
  }
  if (unboundedCount > 3) return { ok: false, reason: 'more than 3 unbounded quantifiers' };
  return { ok: true };
}

/** Input cap for administrator-defined detectors (bounded polynomial worst case). */
const CUSTOM_DETECTOR_MAX_CHARS = 100_000;
